// src/services/studyScreeningService.js
//
// The I/O half of the study page's two screening tools. Everything with a
// decision in it lives in the pure modules next door — eligibilityMatch.js
// (criteria rules -> shortlist) and screeningVisit.js (SoA -> "Needs X") —
// and is tested there. This file only fetches, calls and saves.
//
// WHAT IS STORED WHERE, AND WHY
//
//   studies/{id}.eligibilityQuery   the AI-derived RULES + the unmapped
//                                   criteria + a fingerprint of the criteria
//                                   text they came from. Sponsor-derived, no
//                                   PHI. Cached so the AI runs once per edit.
//   studies/{id}.screeningVisit     the confirmed "Needs X" checklist.
//
// The SHORTLIST ITSELF IS NEVER STORED. `studies` is world-readable by rule
// (`allow read: if true`), so a list of patient names on the study document
// would be a PHI leak. It is recomputed in the browser from the rules each
// time the card opens, which is cheap (one Firestore read the /query page
// already does) and always current.

import { collection, getDocs } from "firebase/firestore";
import { db, auth } from "../firebase";
import { FUNCTIONS_BASE } from "../config/functionsBase";
import { authedFetch } from "./authedFetch";
import { getConsentsPath, getDuplicatesPath } from "./firestorePaths";
import {
  activeSiteUsesLegacySheet,
  getActiveTenantId,
  getActiveSiteId,
} from "./activeTenant";
import { fetchSheetPatients } from "./sheetPatientsService";
import { updateStudy } from "./studyService";
import { normalizePatientForMatching, criteriaFingerprint } from "./eligibilityMatch";

/** The AI call is one short text completion; this is generous. */
const TIMEOUT_MS = 90000;

/**
 * Ask the server to turn a study's criteria into rules.
 *
 * THROWS with the server's own message on every failure. The card shows the
 * message and keeps whatever rules it already had.
 *
 * @param {{ inclusion: string, exclusion: string, disease?: string, studyName?: string }} study
 * @returns {Promise<{ rules: object[], unmapped: object[], meta: object }>}
 */
export const deriveEligibilityQuery = async ({ inclusion, exclusion, disease, studyName }) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let resp;
  try {
    resp = await authedFetch(`${FUNCTIONS_BASE}/deriveEligibilityQuery`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // tenantId/siteId: the server checks membership against the requesting
      // site, not the deployment's own.
      body: JSON.stringify({
        inclusion,
        exclusion,
        disease,
        studyName,
        tenantId: getActiveTenantId(),
        siteId: getActiveSiteId(),
      }),
      signal: controller.signal,
    });
  } catch (e) {
    if (e.name === "AbortError") throw new Error("The AI took too long to answer. Please try again.");
    throw e;
  } finally {
    clearTimeout(timer);
  }
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data.error || `Deriving the query failed (${resp.status}).`);
  return {
    rules: Array.isArray(data.rules) ? data.rules : [],
    unmapped: Array.isArray(data.unmapped) ? data.unmapped : [],
    meta: data.meta || {},
  };
};

/** Persist the derived rules on the study, stamped with what they came from. */
export const saveEligibilityQuery = async (studyId, { rules, unmapped, meta }, { inclusion, exclusion }) => {
  const payload = {
    rules: rules || [],
    unmapped: unmapped || [],
    criteriaFingerprint: criteriaFingerprint(inclusion, exclusion),
    derivedAt: new Date().toISOString(),
    derivedBy: String(auth.currentUser?.email || "").toLowerCase(),
    model: meta?.model || "",
  };
  await updateStudy(studyId, { eligibilityQuery: payload });
  return payload;
};

/** Have the criteria been edited since these rules were derived? */
export const eligibilityQueryIsStale = (study) => {
  const q = study?.eligibilityQuery;
  if (!q || !q.criteriaFingerprint) return false;
  return q.criteriaFingerprint !== criteriaFingerprint(study.INCLUSION, study.EXCLUSION);
};

/** Write the confirmed "Needs X" checklist. */
export const saveScreeningVisit = async (studyId, screeningVisit) => {
  await updateStudy(studyId, { screeningVisit });
};

/**
 * Every patient on the active site, flattened for matching. Same three reads
 * as PatientQueryPage: Firestore consents + duplicates, and the legacy sheet
 * only on the one site that still uses it.
 *
 * The sheet is fail-soft: a Dubois read that can't reach Apps Script still
 * returns the Firestore patients, with `sheetError` set so the card can say
 * the list may be short.
 *
 * @returns {Promise<{ patients: object[], sheetError: string }>}
 */
export const loadPatientsForMatching = async () => {
  const today = new Date();
  const [consSnap, dupSnap] = await Promise.all([
    getDocs(collection(db, getConsentsPath())),
    getDocs(collection(db, getDuplicatesPath())),
  ]);
  const patients = [
    ...consSnap.docs.map((d) => normalizePatientForMatching({ id: d.id, ...d.data() }, "firestore", today)),
    ...dupSnap.docs.map((d) => normalizePatientForMatching({ id: d.id, ...d.data() }, "duplicates", today)),
  ];

  let sheetError = "";
  if (activeSiteUsesLegacySheet()) {
    try {
      const rows = await fetchSheetPatients();
      rows.forEach((r) => patients.push(normalizePatientForMatching(r, "sheet", today)));
    } catch (e) {
      sheetError = e.message || "The legacy sheet could not be read.";
    }
  }
  return { patients, sheetError };
};

/** Where a shortlist row links to. Mirrors how /coordinator opens a patient. */
export const patientDetailPath = (p) =>
  `/coordinator/patient/${encodeURIComponent(p.id)}?src=${encodeURIComponent(p.source)}`;

/**
 * Protocol versions: "I've checked this checklist against Version N and it's
 * still right". Adds four fields to the confirmed checklist and changes
 * nothing else (dotted paths, so the items and the original protocol stay).
 */
export const markScreeningChecked = (studyId, { version, protocolId, by }) =>
  updateStudy(studyId, {
    "screeningVisit.checkedVersion": Number(version),
    "screeningVisit.checkedProtocolId": protocolId || "",
    "screeningVisit.checkedBy": by || "",
    "screeningVisit.checkedAt": new Date().toISOString(),
  });
